// Pure OKR progress helpers — D32 advanced (multi Key Result) + Simple back-compat.
// Decision D32: max 5 KRs per goal; goal score (advanced) = simple mean of each KR's
// clamped progress %; simple mode (no / empty keyResults) = clamp(current/target*100, 0, 100).
import type { Goal, KeyResult } from "../types";

/** Clamp a number to [0, 100]. */
function clamp100(value: number): number {
  return Math.min(100, Math.max(0, value));
}

/**
 * Progress percentage for a single Key Result.
 * Returns a number in [0, 100].
 */
export function keyResultProgress(kr: KeyResult): number {
  const target = Math.max(kr.target, 1); // avoid division-by-zero
  return clamp100((kr.current / target) * 100);
}

/**
 * P5-2a: effective `current` for an auto KR rolled up from a project.
 * Maps the project's completion % onto the KR's own target scale.
 */
export function autoKrCurrent(target: number, projectPercent: number): number {
  return Math.round((projectPercent / 100) * Math.max(target, 1));
}

/**
 * Returns true when the goal is in Advanced OKR mode (has at least one Key Result).
 * Simple mode: keyResults absent or empty array.
 */
export function isAdvancedGoal(goal: Pick<Goal, "keyResults">): boolean {
  return Array.isArray(goal.keyResults) && goal.keyResults.length > 0;
}

/**
 * Aggregate goal progress percentage in [0, 100].
 * - Advanced (has KRs): mean of keyResultProgress() across all KRs.
 * - Simple (no KRs):    clamp(current / target * 100, 0, 100).
 */
export function goalProgress(goal: Pick<Goal, "current" | "target" | "keyResults">): number {
  if (isAdvancedGoal(goal) && goal.keyResults) {
    const sum = goal.keyResults.reduce((acc, kr) => acc + keyResultProgress(kr), 0);
    return clamp100(Math.round(sum / goal.keyResults.length));
  }
  const target = Math.max(goal.target, 1);
  return clamp100(Math.round((goal.current / target) * 100));
}

/**
 * Seed one Key Result from a simple goal's existing metric fields.
 * Used when converting Simple → Advanced mode.
 * The seeded KR mirrors the goal's top-level metric/current/target/unit.
 */
export function seedKeyResultFromGoal(goal: Pick<Goal, "metric" | "current" | "target" | "unit">): KeyResult {
  return {
    id: crypto.randomUUID(),
    name: goal.metric || "Kết quả chính",
    metric: goal.metric || "",
    current: goal.current,
    target: goal.target,
    unit: goal.unit,
  };
}
